import { NavLink, Outlet } from "react-router-dom";
import { Users, ListChecks, LayoutDashboard, Menu, LogOut, Eye, Pencil, ListTodo, ShieldCheck, Clock as HistoryIcon, Building2, Sun, Moon } from "lucide-react";
import { useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Toaster } from "../components/ui/sonner";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { toast } from "sonner";
import GlobalSearch from "./GlobalSearch";
import { previewMode } from "../lib/previewConfig";

const baseNavItems = [
  { to: "/", label: "Visão geral", icon: LayoutDashboard, end: true, testId: "nav-dashboard", roles: ["admin", "employee", "super_admin"] },
  { to: "/escritorios", label: "Escritórios", icon: Building2, testId: "nav-organizations", roles: ["super_admin"] },
  { to: "/clientes", label: "Clientes", icon: Users, testId: "nav-clients", roles: ["admin", "employee"] },
  { to: "/prazos", label: "Prazos", icon: ListChecks, testId: "nav-deadlines", roles: ["admin", "employee"] },
  { to: "/tarefas", label: "Trabalhos", icon: ListTodo, testId: "nav-tasks", roles: ["admin", "employee"] },
  { to: "/usuarios", label: "Usuários", icon: ShieldCheck, testId: "nav-users", roles: ["admin", "super_admin"] },
  { to: "/historico", label: "Histórico", icon: HistoryIcon, testId: "nav-history", roles: ["admin", "super_admin"] },
];

const NavLinks = ({ onClick, role }) => (
  <>
    {baseNavItems
      .filter((n) => n.roles.includes(role || "admin"))
      .map(({ to, label, icon: Icon, end, testId }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onClick}
          data-testid={testId}
          className={({ isActive }) =>
            `verqantis-nav-item flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              isActive
                ? "active bg-[#173b2c] text-white"
                : "text-[#506d5a] hover:bg-white/70 hover:text-[#173b2c]"
            }`
          }
        >
          <Icon className="h-4 w-4" />
          {label}
        </NavLink>
      ))}
  </>
);

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { logout, mode, setMode, user } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const role = user?.role;

  const handleLogout = () => {
    logout();
    setMobileOpen(false);
    toast.success("Sessão encerrada");
  };

  const toggleMode = () => {
    const next = mode === "editor" ? "viewer" : "editor";
    setMode(next);
    toast.success(
      next === "editor" ? "Modo Editor ativado" : "Modo Visualizador ativado"
    );
  };

  return (
    <div className="verqantis-shell min-h-screen text-slate-800 transition-colors">
      <header className="verqantis-topbar sticky top-0 z-40 border-b border-white/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="verqantis-header-inner flex items-center justify-between h-16 gap-3">
            <NavLink to="/" data-testid="app-logo" className="flex items-center gap-3 flex-shrink-0">
              <span className="verqantis-brandmark" aria-hidden="true">V</span>
              <div className="flex flex-col leading-tight">
                <span className="verqantis-brand font-display font-semibold text-base sm:text-lg">
                  Verqantis Operations
                </span>
                <span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden sm:block" data-testid="header-org-name">
                  {user?.role === "super_admin"
                    ? "Painel da Plataforma"
                    : (user?.org_name || "Gestão para Contabilidade")}
                </span>
              </div>
            </NavLink>

            <div className="flex-1 flex justify-center">
              {role !== "super_admin" && <GlobalSearch />}
            </div>

            <nav className="hidden lg:flex flex-col items-stretch gap-1 w-full">
              <NavLinks role={role} />
              <button
                onClick={toggleTheme}
                data-testid="theme-toggle-button"
                title={theme === "dark" ? "Modo claro" : "Modo escuro"}
                className="h-9 w-9 flex items-center justify-center rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700 ml-2 transition-colors"
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <button
                onClick={toggleMode}
                data-testid="mode-toggle-button"
                title={mode === "editor" ? "Alternar para Visualizador" : "Alternar para Editor"}
                className={`flex items-center gap-2 px-3 py-2 ml-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all duration-200 ${
                  mode === "editor"
                    ? "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"
                }`}
              >
                {mode === "editor" ? <Pencil className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {mode === "editor" ? "Editor" : "Visualizador"}
              </button>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="flex items-center gap-2 px-4 py-2 ml-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 transition-all duration-200 border border-slate-200 dark:border-slate-700"
              >
                <LogOut className="h-4 w-4" />
                Sair
              </button>
            </nav>

            <div className="lg:hidden">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <button
                    data-testid="mobile-menu-trigger"
                    className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    <Menu className="h-5 w-5 text-[#0F2942] dark:text-white" />
                  </button>
                </SheetTrigger>
                <SheetContent side="right" className="w-72 dark:bg-slate-900 dark:border-slate-700">
                  <div className="flex flex-col gap-2 mt-8">
                    <NavLinks role={role} onClick={() => setMobileOpen(false)} />
                    <button
                      onClick={toggleTheme}
                      data-testid="theme-toggle-button-mobile"
                      className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all duration-200 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"
                    >
                      {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
                      Tema: {theme === "dark" ? "Escuro" : "Claro"}
                    </button>
                    <button
                      onClick={toggleMode}
                      data-testid="mode-toggle-button-mobile"
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all duration-200 border ${
                        mode === "editor"
                          ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800"
                          : "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"
                      }`}
                    >
                      {mode === "editor" ? <Pencil className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      Modo: {mode === "editor" ? "Editor" : "Visualizador"}
                    </button>
                    <button
                      onClick={handleLogout}
                      data-testid="logout-button-mobile"
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-red-50 hover:text-red-600 transition-all duration-200 border border-slate-200 dark:border-slate-700"
                    >
                      <LogOut className="h-4 w-4" />
                      Sair
                    </button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {previewMode === "staging" && <div role="status" className="verqantis-staging-banner"><span>AMBIENTE DE TESTES</span> Dados fictícios · Backend isolado · Nunca utilize informações de clientes reais</div>}
      <main className="verqantis-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <Outlet />
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}
